import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import UpgradeRequestDialog from '../components/UpgradeRequestDialog';
import { INK, MUTED, LINE, goldButtonSx } from '../constants/brand';

const steps = [
  {
    n: '01',
    title: 'Search',
    description: 'Open Texas and filter wells, production, inspections, violations, severances, and spills.',
  },
  {
    n: '02',
    title: 'Screen',
    description: 'Read the report card, maps, and charts for the whole search, or by county and lease.',
  },
  {
    n: '03',
    title: 'Keep it',
    description: 'Save the search, export a PDF report, or open the raw tables and download a CSV.',
  },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, tierInfo } = useAuth();
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);

  const formatUserName = (account) => {
    const firstName = account?.user_metadata?.first_name;
    const lastName = account?.user_metadata?.last_name;

    if (firstName || lastName) {
      const first = firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase() : '';
      const last = lastName ? lastName.charAt(0).toUpperCase() + lastName.slice(1).toLowerCase() : '';
      return `${first} ${last}`.trim();
    }

    return 'there';
  };

  const tierLabel = tierInfo?.tier
    ? `${tierInfo.tier.charAt(0).toUpperCase()}${tierInfo.tier.slice(1)}`
    : '';

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }}>
      <Typography
        component="h1"
        sx={{
          fontWeight: 750,
          fontSize: { xs: '2rem', md: '2.75rem' },
          letterSpacing: '-0.03em',
          lineHeight: 1.05,
          color: INK,
          mb: 1.5,
        }}
      >
        Welcome back, {formatUserName(user)}.
      </Typography>
      <Typography sx={{ color: MUTED, fontSize: '1.05rem', mb: 5 }}>
        {tierLabel ? (
          <>
            {tierLabel} subscription
            {tierInfo.tier === 'basic' && (
              <>
                {' · '}
                <Button
                  onClick={() => setUpgradeDialogOpen(true)}
                  sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 700, fontSize: 'inherit', verticalAlign: 'baseline' }}
                >
                  Upgrade to Pro
                </Button>
              </>
            )}
          </>
        ) : (
          'Loading subscription...'
        )}
      </Typography>

      <Box
        sx={{
          bgcolor: '#fff',
          border: `1px solid ${LINE}`,
          borderRadius: 2,
          p: { xs: 3, md: 4 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'center' },
          justifyContent: 'space-between',
          gap: 3,
          mb: 2,
        }}
      >
        <Box sx={{ maxWidth: 640 }}>
          <Typography sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.75rem', mb: 1 }}>
            Texas
          </Typography>
          <Typography component="h2" sx={{ fontWeight: 750, fontSize: { xs: '1.5rem', md: '1.85rem' }, letterSpacing: '-0.02em', mb: 1 }}>
            Search the regulatory record
          </Typography>
          <Typography sx={{ color: MUTED, lineHeight: 1.6 }}>
            Wells, production, inspections, violations, lease severances, and spills. Start a screen, then read the flags and the report in the same place.
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() => navigate('/app/search/texas')}
          sx={{ ...goldButtonSx, px: 3, py: 1.25, flexShrink: 0, alignSelf: { xs: 'flex-start', md: 'center' } }}
        >
          Search Texas
        </Button>
      </Box>

      <Box
        sx={{
          borderTop: `1px solid ${LINE}`,
          borderBottom: `1px solid ${LINE}`,
          py: 2.5,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: { xs: 6, md: 8 },
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700 }}>Saved searches</Typography>
          <Typography sx={{ color: MUTED, fontSize: '0.95rem' }}>
            Reopen a search you already built.
          </Typography>
        </Box>
        <Button
          onClick={() => navigate('/app/saved-searches')}
          sx={{ color: 'primary.main', textTransform: 'none', fontWeight: 700, alignSelf: { xs: 'flex-start', sm: 'center' }, px: 0 }}
        >
          View saved searches
        </Button>
      </Box>

      <Typography component="h2" sx={{ fontWeight: 750, fontSize: { xs: '1.45rem', md: '1.7rem' }, letterSpacing: '-0.02em', mb: 3 }}>
        How a screen runs
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: { xs: 3, md: 4 } }}>
        {steps.map((step) => (
          <Box key={step.n}>
            <Typography sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.04em', mb: 1 }}>
              {step.n}
            </Typography>
            <Typography sx={{ fontWeight: 700, mb: 0.75 }}>{step.title}</Typography>
            <Typography sx={{ color: MUTED, lineHeight: 1.6 }}>{step.description}</Typography>
          </Box>
        ))}
      </Box>

      <UpgradeRequestDialog
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
      />
    </Container>
  );
};

export default Dashboard;
